// react
import { useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// components
import { Card, Divider, Icon, Title } from '@/components/atoms';
import { ProfileSummary } from '@/components/molecules';
import { Icons } from '@/components/atoms/Icon';
import { CardContainer } from '@/components/templates';

// models
import { Account } from '@karehero/models';

interface CareProfileProps {
  careRecipient?: Account;
  isCareCircle?: boolean;
  isFinancialStatus?: boolean;
  isAttendanceAllowance?: boolean;
}

/**
 * CareProfile is the page for the care plan.
 */
export const CareProfile = ({
  careRecipient,
  isCareCircle,
  isFinancialStatus,
  isAttendanceAllowance,
}: CareProfileProps) => {
  // memos
  const cards = useMemo(
    () => [
      {
        title: 'Care Assessment',
        to: '/care-assessment',
        color: 'red',
        icon: 'heart',
      },
      ...(isAttendanceAllowance
        ? [
            {
              title: 'Attendance Allowance',
              to: '/attendance-allowance',
              color: 'green',
              icon: 'circle-sterling',
            },
          ]
        : []),
      ...(isFinancialStatus
        ? [
            {
              title: 'Financial Status',
              to: '/financial-status',
              color: 'info',
              icon: 'circle-question',
            },
          ]
        : []),
    ],
    [isCareCircle],
  );

  const altCards = useMemo(
    () => [
      ...(isCareCircle
        ? [
            {
              title: 'Care Circle',
              to: '/care-circle',
              color: 'white',
              icon: 'circle-plus',
            },
          ]
        : []),
      {
        title: 'Key Information',
        to: '/key-information',
        color: 'white',
        icon: 'circle-info',
      },
    ],
    [isCareCircle],
  );

  return (
    <Wrapper>
      <Header>
        <Title>Care Profile</Title>

        <ProfileSummary
          {...careRecipient}
          name={`${careRecipient?.firstName || ''} ${
            careRecipient?.lastName || ''
          }`}
          role='Your Loved One'
          fallback={`${careRecipient?.firstName?.[0] || ''}${
            careRecipient?.lastName?.[0] || ''
          }`}
        />
      </Header>
      <Body>
        <CardContainer maxColumns={1} size='md'>
          {cards.map((card, index) => (
            <StyledLink to={card.to} key={index}>
              <Card color={card.color} isPaddingSm>
                <CardContent>
                  <Content>
                    <Icon icon={card.icon as Icons} />
                    <span>{card.title}</span>
                  </Content>
                  <Icon icon='chevron-right' />
                </CardContent>
              </Card>
            </StyledLink>
          ))}
        </CardContainer>
        <Divider isOr={false} />
        <CardContainer maxColumns={1} size='md'>
          {altCards.map((card, index) => (
            <StyledLink to={card.to} key={index}>
              <Card color={card.color} isPaddingSm>
                <CardContent>
                  <Content>
                    <Icon icon={card.icon as Icons} />
                    <span>{card.title}</span>
                  </Content>
                  <Icon icon='chevron-right' />
                </CardContent>
              </Card>
            </StyledLink>
          ))}
        </CardContainer>
      </Body>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: ${(props) => props.theme.spacing[24]};
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const CardContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${(props) => props.theme.color.neutral[100]};
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
  font: ${(props) => props.theme.typography.body.md.medium};
`;

CareProfile.displayName = 'CareProfile';

export default CareProfile;
